/**
 * `wayai file-types …` — the storage policy and optional metadata schema a
 * file conforms to. Top-level alongside `files`
 * (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { Command } from 'commander';
import { createDataClient } from '../client.js';
import { printOutput } from '../output.js';
import { outputFormat, parseByteCount, parseData, pathSegment, requireBase } from '../helpers.js';
import { confirm } from '../../lib/utils.js';

export function buildFileTypesCommand(): Command {
  const fileTypes = new Command('file-types').description(
    'Manage file types (Buckets) — storage policy + optional metadata schema for files',
  );

  fileTypes
    .command('list')
    .description('List all file types in a base')
    .action(async function (this: Command) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/file-types`), outputFormat(this));
    });

  fileTypes
    .command('get <id>')
    .description('Get a file type')
    .action(async function (this: Command, id: string) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      printOutput(await client.request('GET', `/v1/${base}/file-types/${pathSegment(id, 'file_type')}`), outputFormat(this));
    });

  fileTypes
    .command('upsert <id>')
    .description('Create or update a file type (preview bases only)')
    .requiredOption('--name <name>', 'File type name')
    .option('--description <desc>', 'Description')
    .option('--max-size <bytes>', 'Max bytes per file (0 = unlimited)')
    .option(
      '--allowed-content-types <json>',
      'Allowed content-types, JSON array (inline or @filename)',
    )
    .option('--metadata-schema <json>', 'JSON Schema for file metadata (inline JSON or @filename)')
    .action(async function (
      this: Command,
      id: string,
      opts: {
        name: string;
        description?: string;
        maxSize?: string;
        allowedContentTypes?: string;
        metadataSchema?: string;
      },
    ) {
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      const body: Record<string, unknown> = { name: opts.name };
      if (opts.description) body.description = opts.description;
      // `!== undefined`, not truthiness: `--max-size 0` means unlimited and must
      // still be sent.
      if (opts.maxSize !== undefined) body.max_size = parseByteCount('--max-size', opts.maxSize);
      if (opts.allowedContentTypes) {
        body.allowed_content_types = parseData(opts.allowedContentTypes);
      }
      if (opts.metadataSchema) body.metadata_schema = parseData(opts.metadataSchema);
      printOutput(
        await client.request('PUT', `/v1/${base}/file-types/${pathSegment(id, 'file_type')}`, body),
        outputFormat(this),
      );
    });

  fileTypes
    .command('delete <id>')
    .description('Delete a file type (cascades its files)')
    .option('-y, --yes', 'Skip confirmation prompt')
    .action(async function (this: Command, id: string, opts: { yes?: boolean }) {
      if (
        !opts.yes &&
        !(await confirm(
          `Delete file type "${id}"? This cascades its files and cannot be undone.`,
        ))
      ) {
        console.log('Aborted');
        return;
      }
      const base = pathSegment(requireBase(this), '--base');
      const client = await createDataClient();
      await client.request('DELETE', `/v1/${base}/file-types/${pathSegment(id, 'file_type')}`);
      console.log('Deleted');
    });

  return fileTypes;
}
